import { fillOf, type StorageCell, type StorageDrive, type StorageView } from "../../shared/storage.js";
import { Page } from "../Page.js";
import { storageChart } from "../components/StorageChart.js";
import { h } from "../dom.js";
import { ago, compact, duration, full } from "../format.js";

const SIDES = ["below", "above", "north", "south", "west", "east"];
const NEARLY_FULL = 0.9;

interface Row {
  cell: StorageCell;
  drive: StorageDrive;
  driveNo: number;
}

function share(used: number | null, total: number | null): number {
  return total ? (used ?? 0) / total : 0;
}

function bar(used: number | null, total: number | null, text: string): HTMLElement {
  const f = share(used, total);
  const cls = f >= NEARLY_FULL ? "low" : f >= 0.75 ? "warn" : "";
  return h(
    "div",
    { class: "fill-cell" },
    h("span", { class: "small" }, text),
    h("div", { class: "bar" }, h("div", { class: `fill ${cls}`, style: `width:${Math.min(100, f * 100)}%` })),
  );
}

/** "3 days", "5 hours": one unit is enough for a forecast. */
function roughly(ms: number): string {
  const days = ms / 86_400_000;
  if (days >= 2) return `${Math.round(days)} days`;
  return duration(ms);
}

function percent(f: number): string {
  return `${(f * 100).toFixed(f >= 0.995 && f < 1 ? 1 : 0)}%`;
}

/**
 * How full the network's storage cells are, read by the Butler through the
 * transposers placed against the ME Drives: totals, the growth trend, and
 * every cell, fullest first.
 */
export class StoragePage extends Page {
  readonly title = "Storage";
  private data: StorageView | null = null;
  private readonly sort = h("select", null, h("option", { value: "full" }, "Sort: fullest first"), h("option", { value: "drive" }, "Sort: by drive"));
  private readonly onlyFull = h("input", { type: "checkbox" });
  private readonly refreshButton = h("button", { onclick: () => void this.refresh() }, "Read now");
  private readonly notice = h("div");
  private readonly tiles = h("div", { class: "cards storage-tiles" });
  private readonly chartBox = h("div", { class: "card" });
  private readonly list = h("div");

  mount(): void {
    this.sort.addEventListener("change", () => this.renderList());
    this.onlyFull.addEventListener("change", () => this.renderList());
    this.el.append(
      h("div", { class: "row" }, h("h2", null, "Storage"), h("span", { class: "spacer" }), this.refreshButton),
      h(
        "p",
        { class: "muted small" },
        "How full every storage cell is, read through Transposers placed against your ME Drives and ME Chests (AE shows OpenComputers no other way to them). " +
          "A cell counts as full when its bytes or its item types run out, whichever comes first. Read every 10 minutes while the game is connected.",
      ),
      this.notice,
      this.tiles,
      this.chartBox,
      h("div", { class: "card" }, h("div", { class: "toolbar" }, h("h3", null, "Cells"), h("span", { class: "spacer" }), this.sort, h("label", { class: "check small" }, this.onlyFull, "Only nearly full")), this.list),
    );
    this.listen("storage", () => void this.load());
    this.listen("link", () => void this.load());
    void this.load();
  }

  private async load(): Promise<void> {
    const data = await this.attempt(() => this.ctx.api.storage());
    if (!data) return;
    this.data = data;
    this.render();
  }

  private async refresh(): Promise<void> {
    this.refreshButton.disabled = true;
    this.refreshButton.textContent = "Reading…";
    const data = await this.attempt(() => this.ctx.api.refreshStorage(), "Storage cells read");
    this.refreshButton.disabled = false;
    this.refreshButton.textContent = "Read now";
    if (data) {
      this.data = data;
      this.render();
    }
  }

  private render(): void {
    const d = this.data;
    if (!d) return;
    const last = d.last;
    this.refreshButton.disabled = d.loading || d.unavailable !== null;
    this.refreshButton.title = d.unavailable ?? "";
    const notes: HTMLElement[] = [];
    if (d.unavailable) notes.push(h("p", { class: "small warn" }, `Can't read the cells now: ${d.unavailable}.`));
    if (d.error) notes.push(h("p", { class: "small error" }, `The last read failed: ${d.error}`));
    if (last && last.transposers === 0)
      notes.push(h("p", { class: "small warn" }, "The Butler found no Transposer. Place one against each ME Drive (one transposer reads every drive on its six sides) and connect it to the computer with cable."));
    else if (last && last.drives.length === 0)
      notes.push(h("p", { class: "small warn" }, `The Butler found ${last.transposers} transposer${last.transposers === 1 ? "" : "s"}, but none touches an ME Drive or ME Chest.`));
    this.notice.replaceChildren(...notes);

    if (!last) {
      this.tiles.replaceChildren(h("div", { class: "card" }, h("p", { class: "muted" }, d.unavailable ? "No read yet." : "No read yet. Click Read now.")));
      this.chartBox.hidden = true;
      this.list.replaceChildren();
      return;
    }
    const t = last.totals;
    const bytes = share(t.usedBytes, t.totalBytes);
    const types = share(t.usedTypes, t.totalTypes);
    const tile = (title: string, value: string, sub: HTMLElement | string, fill?: number) =>
      h(
        "div",
        { class: "card tile" },
        h("div", { class: "muted small" }, title),
        h("div", { class: "tile-value" }, value),
        fill !== undefined ? h("div", { class: "bar big" }, h("div", { class: `fill ${fill >= NEARLY_FULL ? "low" : fill >= 0.75 ? "warn" : ""}`, style: `width:${Math.min(100, fill * 100)}%` })) : null,
        h("div", { class: "muted small" }, sub),
      );
    const trend =
      d.fullAt !== null
        ? `Full in about ${roughly(d.fullAt - Date.now())} at the pace of the last week`
        : d.bytesPerDay !== null
          ? "Not growing over the last week"
          : "The trend shows after a few reads";
    this.tiles.replaceChildren(
      tile("Bytes used", percent(bytes), `${compact(t.usedBytes)} of ${compact(t.totalBytes)} bytes`, bytes),
      tile("Item types used", percent(types), `${full(t.usedTypes)} of ${full(t.totalTypes)} types`, types),
      tile(
        "Cells",
        full(t.cells),
        `${t.nearlyFull} nearly full, ${t.freeSlots} free slot${t.freeSlots === 1 ? "" : "s"} in ${t.drives} drive${t.drives === 1 ? "" : "s"}` +
          (t.unreadable ? `, ${t.unreadable} fluid or essentia (can't be read)` : ""),
      ),
      tile(
        "Growth",
        d.bytesPerDay !== null ? `${d.bytesPerDay >= 0 ? "+" : "−"}${compact(Math.abs(d.bytesPerDay))} B/day` : "–",
        h("span", null, trend, d.typesPerDay !== null ? `, ${d.typesPerDay >= 0 ? "+" : "−"}${compact(Math.abs(d.typesPerDay))} types/day` : "", `. Read ${ago(last.at)}.`),
      ),
    );
    this.chartBox.hidden = false;
    this.chartBox.replaceChildren(h("h3", null, "Last 30 days"), storageChart(d.samples));
    this.renderList();
  }

  private renderList(): void {
    const last = this.data?.last;
    if (!last) return;
    let rows: Row[] = last.drives.flatMap((drive, i) => drive.cells.map((cell) => ({ cell, drive, driveNo: i + 1 })));
    if (this.onlyFull.checked) rows = rows.filter((r) => r.cell.readable && fillOf(r.cell) >= NEARLY_FULL);
    if (this.sort.value === "full") rows.sort((a, b) => Number(b.cell.readable) - Number(a.cell.readable) || fillOf(b.cell) - fillOf(a.cell));
    if (!rows.length) {
      this.list.replaceChildren(h("p", { class: "muted empty" }, last.drives.length ? (this.onlyFull.checked ? "No cell is nearly full." : "The drives hold no cell.") : "No drive found."));
      return;
    }
    const driveTitle = (r: Row) => `Transposer ${r.drive.transposer.slice(0, 8)}, ${SIDES[r.drive.side] ?? `side ${r.drive.side}`}: ${r.drive.inventory}`;
    this.list.replaceChildren(
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          { class: "storage" },
          h("thead", null, h("tr", null, h("th", null, "Cell"), h("th", null, "Drive"), h("th", null, "Bytes"), h("th", null, "Types"), h("th", { class: "num" }, "Items"))),
          h(
            "tbody",
            null,
            ...rows.map((r) => {
              const c = r.cell;
              return h(
                "tr",
                { class: c.readable && fillOf(c) >= NEARLY_FULL ? "nearly-full" : "" },
                h("td", null, c.label),
                h("td", { title: driveTitle(r) }, `Drive ${r.driveNo}, slot ${c.slot}`),
                c.readable
                  ? h("td", null, bar(c.usedBytes, c.totalBytes, `${compact(c.usedBytes ?? 0)} / ${compact(c.totalBytes ?? 0)} · ${percent(share(c.usedBytes, c.totalBytes))}`))
                  : h("td", { class: "muted small", colspan: "2" }, "Fluid or essentia cell: OpenComputers can't read its fill"),
                c.readable ? h("td", null, bar(c.usedTypes, c.totalTypes, `${c.usedTypes} / ${c.totalTypes}`)) : null,
                h("td", { class: "num" }, c.count === null ? "–" : compact(c.count)),
              );
            }),
          ),
        ),
      ),
    );
  }
}
